<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">

      <div class="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
        <div>
          <h2 class="text-lg font-bold text-slate-900 flex items-center gap-2">
            <CreditCard class="w-5 h-5 text-indigo-600" />
            {{ form.id ? 'Editar Plan' : 'Nuevo Plan' }}
          </h2>
          <p class="text-xs text-slate-500">Configura límites y precios de la membresía.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="planForm" @submit.prevent="handleSubmit" class="space-y-6">

          <!-- Bloque Comercial -->
          <div class="bg-white p-5 rounded-2xl border border-slate-200">
            <h3 class="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">1. Datos Comerciales</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label :class="labelClasses">Nombre del Plan</label>
                <input v-model="form.name" type="text" :class="inputClasses" placeholder="Ej: Básico" required />
              </div>
              <div>
                <label :class="labelClasses">Precio Mensual (USD)</label>
                <input v-model="form.price" type="number" step="0.01" :class="inputClasses" placeholder="0.00" required />
              </div>
              <div class="sm:col-span-2">
                <label :class="labelClasses">Descripción Corta</label>
                <input v-model="form.description" type="text" :class="inputClasses" placeholder="Ideal para negocios pequeños..." />
              </div>
              <div class="sm:col-span-2">
                <label class="flex items-center gap-2 cursor-pointer">
                  <input v-model="form.is_active" type="checkbox" :true-value="1" :false-value="0" class="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500">
                  <span class="text-sm font-bold text-slate-700">Plan Activo y Visible</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Bloque de Límites Técnicos -->
          <div class="bg-indigo-50/50 p-5 rounded-2xl border border-indigo-100">
            <h3 class="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-4 border-b border-indigo-100 pb-2">2. Límites del Asistente (Wizard)</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">

              <div>
                <label :class="labelClasses">Máximo Instancias WhatsApp</label>
                <div class="relative">
                  <Smartphone class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.max_instances" type="number" :class="inputIconClasses" min="1" required />
                </div>
              </div>

              <div>
                <label :class="labelClasses">Límite de Productos (Catálogo)</label>
                <div class="relative">
                  <ShoppingBag class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.max_products" type="number" :class="inputIconClasses" min="1" required />
                </div>
              </div>

              <div>
                <label :class="labelClasses">Límite Reglas de Negocio / Políticas</label>
                <div class="relative">
                  <ShieldAlert class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.max_rules" type="number" :class="inputIconClasses" min="1" required />
                </div>
              </div>

              <div>
                <label :class="labelClasses">Máx Palabras por Respuesta (IA)</label>
                <div class="relative">
                  <MessageSquare class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input v-model="form.max_words_response" type="number" :class="inputIconClasses" min="10" max="300" required />
                </div>
                <p class="text-[10px] text-slate-500 mt-1">Controla tu gasto de tokens en Groq.</p>
              </div>

            </div>
          </div>

        </form>
      </div>

      <div class="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 bg-white">
        <button @click="$emit('close')" type="button" :class="btnGhost">Cancelar</button>
        <button type="submit" form="planForm" :disabled="isSaving" :class="btnPrimary">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin mr-2" />
          {{ form.id ? 'Guardar Cambios' : 'Crear Plan' }}
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import {
  X, Loader2, CreditCard, Smartphone, ShoppingBag, ShieldAlert, MessageSquare
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';

const labelClasses = "text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block";
const inputClasses = "w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-slate-400";
const inputIconClasses = "w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-slate-900 rounded-xl hover:bg-slate-800 shadow-lg transition-all flex items-center justify-center disabled:opacity-70";
const btnGhost = "px-6 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors";

const props = defineProps({ show: Boolean, planToEdit: Object });
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);

const form = reactive({
  id: null,
  name: '',
  description: '',
  price: 0.00,
  max_instances: 1,
  max_products: 5,
  max_rules: 5,
  max_words_response: 45,
  is_active: 1
});

watch(() => props.show, (val) => {
  if (val) {
    if (props.planToEdit) {
      Object.assign(form, props.planToEdit);
    } else {
      Object.assign(form, {
        id: null, name: '', description: '', price: 0.00,
        max_instances: 1, max_products: 5, max_rules: 5, max_words_response: 45, is_active: 1
      });
    }
  }
});

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const payload = { ...form };
    if (!form.id) delete payload.id;

    if (form.id) {
      await apiClient.put(`/plans/${form.id}`, payload);
      showSuccess('Plan actualizado');
    } else {
      await apiClient.post('/plans', payload);
      showSuccess('Nuevo plan creado');
    }
    emit('saved');
    emit('close');
  } catch (error) {
    showError('Error al guardar el plan');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
</style>